import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import RecentActorsOutlined from "@mui/icons-material/RecentActorsOutlined";
import StreamOutlined from "@mui/icons-material/StreamOutlined";

import {
  Box,
  Button,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

import { setTab } from "../../store/UI";
import { UIStore } from "../../types/store";

import { RequestQuoteOutlined } from "@mui/icons-material";
import AddressesTab from "../../components/Entities/Tabs/AddressesTab";
import CapturesTab from "../../components/Entities/Tabs/CapturesTab";
import ContactsTab from "../../components/Entities/Tabs/ContactsTab";
import DocumentsTab from "../../components/Entities/Tabs/DocumentsTab";
import FilesTab from "../../components/Entities/Tabs/FilesTab";
import GeneralTab from "../../components/Entities/Tabs/GeneralTab";
import ManagementUnitsTab from "../../components/Entities/Tabs/ManagementUnitsTab";
import ProtectionZoneTab from "../../components/Entities/Tabs/ProtectionZoneTab";
import ResponsiblesTab from "../../components/Entities/Tabs/ResponsiblesTab";
import { usePermissions } from "../../hooks";
import { useEntity } from "../../hooks/Entity/useEntity";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";

export default function DetailsEntity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { canViewEntityList } = useAccountPermissions();

  const { entityId } = useParams<{ entityId: string }>();
  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [tabIndex, setTabIndex] = useState(tabState[location.pathname] || 0);
  const [open, setOpen] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const { entityData, update, isPending, isUpdating, isError, error, refetch } = useEntity(entityId);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  const handleDrawerToggle = () => {
    setIsTransitioning(true);
    setOpen(!open);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
  };
  
  const drawerItems = [
    { 
      text: t("entities.tabs.general"), 
      icon: <InfoOutlined />, 
      index: 0,
    },
    {
      text: t("entities.tabs.addresses"),
      icon: <LocationOnOutlined />,
      index: 1,
      permission: "ENTITY_TAB_ADDRESSES"
    },
    {
      text: t("entities.tabs.contacts"),
      icon: <RecentActorsOutlined />,
      index: 2,
      permission: "ENTITY_TAB_CONTACTS"
    },
    {
      text: t("entities.tabs.responsibles"),
      icon: <PeopleAltOutlined />,
      index: 3,
      permission: "ENTITY_TAB_RESPONSIBLES"
    },
    { 
      text: t("entities.tabs.idDocuments"), 
      icon: <BadgeOutlined />, 
      index: 4,
      permission: "ENTITY_TAB_DOCUMENTS"
    },
    {
      text: t("entities.tabs.management-units"),
      icon: <AccountTreeOutlined />,
      index: 5,
      permission: "ENTITY_TAB_MANAGEMENT_UNITS"
    },
    { 
      text: t("entities.tabs.captures"), 
      icon: <StreamOutlined />, 
      index: 6,
      permission: "ENTITY_TAB_CAPTURES"
    },
    {
      text: t("entities.tabs.protection-zone"),
      icon: <RequestQuoteOutlined />,
      index: 7,
      permission: "ENTITY_TAB_PROTECTION_ZONE"
    },
    { 
      text: t("entities.tabs.files"),
      icon: <AttachFileOutlined />,
      index: 8,
      permission: "ENTITY_TAB_FILES"
    },
  ].filter(item => hasPermission(item.permission));

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          edge="start"
          sx={{
            marginRight: 2,
          }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>

        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("entities.label")}
          {entityData ? ` - ${entityData.name.toUpperCase()}` : ""}
        </Typography>

        {canViewEntityList && (
          <Tooltip title={t("generic.back")}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              <ArrowBackIcon />
            </Button>
          </Tooltip>
        )}
      </Toolbar>

      <Box sx={{ display: "flex" }}>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? 240 : (theme) => `calc(${theme.spacing(7)} + 1px)`,
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: open
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              position: "relative",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
              width: "inherit",
              overflowX: "hidden",
              border: "none",
              zIndex: "auto",
            },
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          <List component="nav">
            {drawerItems.map((item) => (
              <ListItem key={item.index} disablePadding>
                <Tooltip title={open ? "" : item.text} placement="left">
                  <ListItemButton
                    selected={tabIndex === item.index}
                    onClick={(e) => handleTabChange(e, item.index)}
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5,
                      alignItems: "center",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: (theme) =>
                          theme.transitions.create("opacity"),
                        whiteSpace:
                          open && !isTransitioning ? "normal" : "nowrap",
                        overflow: "hidden",
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Drawer>

        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Fade in={tabIndex === 0} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
            <Box>
              <GeneralTab
                entityData={entityData}
                onUpdate={update}
                isPending={isPending}
                isUpdating={isUpdating}
                isError={isError}
                error={error}
                refetch={refetch}
              />
            </Box>
          </Fade>

          {hasPermission("ENTITY_TAB_ADDRESSES") && (
            <Fade in={tabIndex === 1} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box>
                <AddressesTab
                  entityId={entityId}
                  entityName={entityData?.name}
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_CONTACTS") && (
            <Fade in={tabIndex === 2} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box>
                <ContactsTab
                  entityId={entityId}
                  entityName={entityData?.name}
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_RESPONSIBLES") && (
            <Fade in={tabIndex === 3} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box>
                <ResponsiblesTab
                  entityId={entityId}
                  entityName={entityData?.name}
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_DOCUMENTS") && (
            <Fade in={tabIndex === 4} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box>
                <DocumentsTab
                  entityId={entityId}
                  entityName={entityData?.name}
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_MANAGEMENT_UNITS") && (
            <Fade in={tabIndex === 5} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                <ManagementUnitsTab
                  entityId={entityId}
                  entityName={entityData?.name}
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_CAPTURES") && (
            <Fade in={tabIndex === 6} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                <CapturesTab
                  entityId={entityId}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_PROTECTION_ZONE") && (
            <Fade in={tabIndex === 7} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                <ProtectionZoneTab 
                  entityId={entityId} 
                  handleError={handleError}
                />
              </Box>
            </Fade>
          )}

          {hasPermission("ENTITY_TAB_FILES") && (
            <Fade in={tabIndex === 8} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {entityData && (
                  <FilesTab
                    entityId={Number.parseInt(entityId!)}
                  />
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
